import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link, NavLink } from 'react-router-dom';
import ArrowRight from '@mui/icons-material/ArrowRight';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

import { Menu, MenuItem } from 'uikit/menus';
import { Button } from 'uikit/buttons';
import { Icon } from 'uikit/icons';
import { Typo } from 'uikit/content';

import styles from '../styles/menuBreadcrumbs.module.scss';

const BreadcrumbLink = ({ breadcrumb }) => {
  const Element = breadcrumb.isDisabled ? React.Fragment : NavLink;
  return (
    <div className={styles.link}>
      <Element to={breadcrumb.url} className={styles.link} title={breadcrumb.name}>
        <Typo variant="s03" className={styles.linkText}>{breadcrumb.name}</Typo>
      </Element>
      <KeyboardArrowRight sx={{ height: 16, width: 16 }} />
    </div>
  );
};

const MenuBreadcrumbs = ({ breadcrumbs = [], className = '', lastBreadcrumbMenuItems = [] }) => {
  const freshBreadcrumbs = [...breadcrumbs];
  const totalCrumbsCount = freshBreadcrumbs.length;

  if (totalCrumbsCount === 0) return null;

  const shouldShowKebabMenu = totalCrumbsCount > 4;

  const firstCrumb = freshBreadcrumbs.shift();
  const lastCrumb = freshBreadcrumbs.pop();
  const secondCrumb = shouldShowKebabMenu ? null : freshBreadcrumbs.shift();
  const penultimateCrumb = freshBreadcrumbs.pop();
  return (
    <nav className={classnames(styles.element, className)}>
      <BreadcrumbLink breadcrumb={firstCrumb} />
      {secondCrumb && (<BreadcrumbLink breadcrumb={secondCrumb} />)}
      {shouldShowKebabMenu && (
        <div className={styles.intermediaryMenu}>
          <Menu
            horizontalPosition="left"
            component={(
              <Button
                icon={<MoreHoriz sx={{ height: 18, width: 18 }} />}
                size="small"
                variant="ghost"
                id="menu-breadcrumbs-kebab-menu-button"
              />
      )}
          >
            {freshBreadcrumbs.map((breadcrumb) => {
              const Element = breadcrumb.isDisabled ? Typo : Link;
              return (
                <MenuItem key={breadcrumb.id}>
                  <Element
                    to={breadcrumb.url}
                    className={classnames(styles.link, {
                      [styles.isDisabled]: breadcrumb.isDisabled,
                    })}
                  >
                    {breadcrumb.name}
                  </Element>
                </MenuItem>
              );
            })}
          </Menu>
          <KeyboardArrowRight className={styles.linkIcon} sx={{ height: 16, width: 16 }} />
        </div>
      )}
      {penultimateCrumb && (<BreadcrumbLink breadcrumb={penultimateCrumb} />)}
      {lastCrumb && (
        <Menu
          className={styles.lastCrumbMenu}
          component={(
            <Button
              id="menu-breadcrumbs-last-crumb-menu-button"
              className={styles.lastCrumbMenuButton}
              title={lastCrumb.name}
              icon={<ArrowRight sx={{ height: 18, width: 18 }} />}
              size="small"
              iconPosition="right"
              variant="ghost"
              text={lastCrumb.name}
              isDisabled={lastCrumb.isDisabled}
            />
        )}
        >
          {lastBreadcrumbMenuItems}
        </Menu>
      )}
    </nav>
  );
};

MenuBreadcrumbs.propTypes = {
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    url: PropTypes.string,
  })),
  className: PropTypes.string,
  lastBreadcrumbMenuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    url: PropTypes.string,
  })),
};

export default React.memo(MenuBreadcrumbs);
